import { WeightPerWeekData } from 'views/bluviu-nutrition/features/scenarios-v2/types/weight-per-week-graph.type';

export type WeightPerWeekSeries = { name: string; data: [number, number][] };

export const indexWeightDataByFeedProgram = (graphData: WeightPerWeekData[]): Map<string, WeightPerWeekData> =>
  new Map(graphData.map((d) => [d.feedPlan, d]));

const siteAvgPerWeek = (planData: WeightPerWeekData): [number, number][] =>
  planData.graphValues.map((point) => [point.productionWeek, Math.round(point.weight)]);

const weightedAvgPerWeek = (planData: WeightPerWeekData, selectedGroupIds: Set<string>): [number, number][] => {
  const aggregatesByWeek = new Map<number, { numerator: number; denominator: number }>();

  for (const point of planData.fishGroupGraphValues ?? []) {
    if (!selectedGroupIds.has(point.fishGroupId)) continue;
    const agg = aggregatesByWeek.get(point.productionWeek) ?? { numerator: 0, denominator: 0 };
    agg.numerator += point.weightNumerator;
    agg.denominator += point.fishEndWeek;
    aggregatesByWeek.set(point.productionWeek, agg);
  }

  const result: [number, number][] = [];
  for (const [week, agg] of aggregatesByWeek) {
    if (agg.denominator > 0) {
      result.push([week, Math.round(agg.numerator / agg.denominator)]);
    }
  }
  return result.sort((a, b) => a[0] - b[0]);
};

const buildWeightPerWeekSeries = ({
  feedPlanNames,
  fishGroupIds,
  allFishGroupIds,
  graphWeightIndex,
}: {
  feedPlanNames: string[];
  fishGroupIds: string[];
  allFishGroupIds: string[];
  graphWeightIndex: Map<string, WeightPerWeekData>;
}): WeightPerWeekSeries[] => {
  const isAllFishGroups = allFishGroupIds.length > 0 && fishGroupIds.length === allFishGroupIds.length;

  const selectedGroupIds = isAllFishGroups ? null : new Set(fishGroupIds);

  const result: WeightPerWeekSeries[] = [];

  for (const planName of feedPlanNames) {
    const planData = graphWeightIndex.get(planName);
    if (!planData) continue;

    const data = isAllFishGroups ? siteAvgPerWeek(planData) : weightedAvgPerWeek(planData, selectedGroupIds!);

    result.push({ name: planName, data });
  }

  return result.sort((a, b) => a.name.localeCompare(b.name));
};

export default buildWeightPerWeekSeries;
